import React, { Fragment, useState, useEffect } from "react";
import { backend, API_URL } from "../../../config";
import {
  Button,
  Typography,
  Modal,
  CircularProgress,
  Container,
  Grid,
  TextField,
  InputLabel,
  Autocomplete,
} from "@mui/material";

import JSZip from "jszip";

import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import ManagementNav from "../../../features/OrderManagement/components/Nav/ManagementNav";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  layout: {
    width: "auto",
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600)]: {
      width: 900,
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(5),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

// todo: combine this function with sendRequest
export const downloadInvoices = async (orders, dateFile = null) => {
  if (!orders?.length) return;

  return Promise.all(
    orders.map(
      async (orderObj) =>
        await backend
          .get(`${API_URL}orders/${orderObj.id}/invoice/downloadexport`, {
            responseType: "blob",
          })
          .then(({ data }) => ({
            filename: `Invoice #${orderObj.id || ""} ${
              orderObj.customer.username || ""
            }.csv`,
            file: data,
          }))
          .catch((error) => {
            console.log(error);
          }),
    ),
  ).then(async (result) => {
    const hasFiles = result.some((fileObj) => fileObj !== undefined);

    if (hasFiles) {
      const zipFile = new JSZip();

      result.forEach((fileObj) => zipFile.file(fileObj.filename, fileObj.file));

      let deliveryDateObj = dateFile === null ? new Date() : new Date(dateFile);

      const deliveryDateParam = new Date(
        deliveryDateObj.getTime() - deliveryDateObj.getTimezoneOffset() * 60000,
      )
        .toISOString()
        .split("T")[0];

      return await zipFile.generateAsync({ type: "blob" }).then((content) => {
        const blob = new Blob([content], { type: "application/zip" });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = blobUrl;
        link.setAttribute("download", `${deliveryDateParam}_export.zip`);
        link.click();
        link.remove();

        return true;
      });
    }
  });
};

export const ExportInvoices = (props) => {
  const classes = useStyles();

  const cDate = new Date();

  const [confirmModal, setConfirmModal] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(cDate);
  const [orders, setOrders] = useState([]);
  const [ordersSelected, setOrdersSelected] = useState([]);
  const [ordersExcluded, setOrdersExcluded] = useState([]);

  const handleChangeOrders = (e, value) => {
    setOrdersSelected(value);
  };

  const handleChangeDate = (e) => {
    const { value } = e.target;
    setOrders([]);
    setOrdersSelected([]);
    setDeliveryDate(value);
  };

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Are you sure you want to bulk export invoices?
          <div align="right">
            <Button
              onClick={() => toggleConfirmModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
              className={classes.button}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Exporting Invoices <br />
              This may take some time relative to the number of invoices
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while exporting invoices
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "scroll",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <b>Error:</b>
                <br />
                <div dangerouslySetInnerHTML={{ __html: errorInfo.message }} />
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveRequest()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The invoices were exported successfully.
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const resolveRequest = () => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    toggleConfirmModal();
    setErrorInfo({ error: false, message: "" });
  };

  const sendRequest = async () => {
    setLoadingFlag(true);

    Promise.all(
      ordersSelected.map(
        async (orderObj) =>
          await backend
            .get(`${API_URL}orders/${orderObj.id}/invoice/downloadexport`, {
              responseType: "blob",
            })
            .then(({ data }) => ({
              filename: `Invoice #${orderObj.id || ""} ${
                orderObj.customer.username || ""
              }.csv`,
              file: data,
            }))
            .catch((error) => {
              console.log(error);
              const errorData = error.response.data;

              setErrorInfo((prevState) => {
                let newMsg =
                  typeof errorData === "object"
                    ? errorData.hasOwnProperty("message")
                      ? errorData.message
                      : errorData.detail
                    : error.message;

                if (newMsg === undefined) {
                  const orderId = error.response.config.url
                    .replace("https://fforders.com/api/orders/", "")
                    .replace("/invoice/downloadexport", "");

                  if (!prevState.message.includes(orderId)) {
                    newMsg = `Invoice ${orderId}: ${error.response.statusText}`;
                  } else {
                    newMsg = "";
                  }
                }

                if (newMsg !== "") {
                  const errorMsg =
                    prevState.message +
                    `${prevState.message === "" ? "" : "<br />"}` +
                    newMsg;

                  return {
                    error: true,
                    message: errorMsg,
                  };
                }

                return prevState;
              });
            }),
      ),
    ).then(async (result) => {
      const hasFiles = result.some((fileObj) => fileObj !== undefined);

      if (hasFiles) {
        const zipFile = new JSZip();

        result.forEach((fileObj) =>
          zipFile.file(fileObj.filename, fileObj.file),
        );

        let deliveryDateObj = new Date(deliveryDate);

        const deliveryDateParam = new Date(
          deliveryDateObj.getTime() -
            deliveryDateObj.getTimezoneOffset() * 60000,
        )
          .toISOString()
          .split("T")[0];

        await zipFile.generateAsync({ type: "blob" }).then((content) => {
          const blob = new Blob([content], { type: "application/zip" });
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");

          link.href = blobUrl;
          link.setAttribute("download", `${deliveryDateParam}_export.zip`);
          link.click();
          link.remove();

          setLoadingFlag(false);
          setFinishedFlag(true);
        });
      }
    });
  };

  useEffect(() => {
    const getOrders = async () => {
      let deliveryDateObj = new Date(deliveryDate);

      const deliveryDateParam = new Date(
        deliveryDateObj.getTime() - deliveryDateObj.getTimezoneOffset() * 60000,
      )
        .toISOString()
        .split("T")[0];

      await backend
        .get(
          `${API_URL}orders?no_page=1&start_date=${deliveryDateParam}&end_date=${deliveryDateParam}`,
        )
        .then(({ data }) => {
          if (data.length) {
            let oSelected = [];
            let oExcluded = [];

            data.forEach((item) => {
              if (item.invoiced) {
                oSelected.push(item);
              } else {
                oExcluded.push(
                  `${item.customer ? item.customer.username : "Anonymous"} #${
                    item.id
                  }`,
                );
              }
            });

            setOrders(oSelected);
            setOrdersSelected(oSelected);
            setOrdersExcluded(oExcluded);
          }
        });
    };

    if (deliveryDate !== null) {
      getOrders();
    }
  }, [deliveryDate]);

  return (
    <Container style={{ minHeight: "80vh" }}>
      <Modal open={confirmModal} onClose={toggleConfirmModal}>
        {confirmModalContent}
      </Modal>
      <ManagementNav {...props} />
      <Typography component="h6" variant="h6" align="center">
        Export Invoices
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Use this interface to export all invoices in CSV format contained
          within a zip file.
        </Grid>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Load orders from a given date.
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              value={deliveryDate}
              renderInput={(props) => (
                <TextField
                  {...props}
                  fullWidth
                  label="Delivery date"
                  variant="standard"
                />
              )}
              onChange={(date) =>
                handleChangeDate({
                  target: {
                    name: "delivery_date",
                    value: date,
                  },
                })
              }
            />
          </LocalizationProvider>
        </Grid>
        {deliveryDate !== null ? (
          <>
            <Grid item xs={12} sm={12}>
              <InputLabel shrink id="orders">
                Orders
              </InputLabel>
              <Autocomplete
                fullWidth
                multiple
                disablePortal
                size="small"
                name="orders"
                options={orders}
                getOptionLabel={(option) =>
                  `${
                    option.customer ? option.customer.username : "Anonymous"
                  } #${option.id}`
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" variant="standard" />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(e, value) => handleChangeOrders(e, value)}
                helpertext="Select the orders to include in the receipt difference."
                value={ordersSelected}
              />
            </Grid>
            {ordersExcluded.length > 0 && (
              <Grid item xs={12}>
                <b>The following orders are not invoiced yet:</b>
                {` ${ordersExcluded.join(", ")}`}
              </Grid>
            )}
          </>
        ) : (
          <></>
        )}
        <Grid item xs={12} sm={12} style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => toggleConfirmModal()}
            className={classes.button}
            disabled={!ordersSelected.length || loadingFlag}
          >
            Export Invoices
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ExportInvoices;
